/**
 * Route-Aware Prefetch Utility
 * 
 * PERFORMANCE: This utility replaces webpack's default prefetch behavior
 * which loads ALL lazy-loaded chunks (125+ requests) with selective prefetching
 * based on the current route context.
 * 
 * Candidate assessment pages only prefetch assessment-related chunks.
 * Admin pages prefetch admin-related chunks.
 */

import { isInitialPublicAuthPath } from "@/utils/routeFlags";
import { normalizeLanguageKey } from "@/helpers/language";

// Type declaration for requestIdleCallback (not in default TypeScript lib)
type RequestIdleCallbackHandle = number;
type RequestIdleCallbackOptions = {
  timeout?: number;
};
type RequestIdleCallbackDeadline = {
  readonly didTimeout: boolean;
  timeRemaining: () => number;
};

declare global {
  interface Window {
    requestIdleCallback?: (
      callback: (deadline: RequestIdleCallbackDeadline) => void,
      opts?: RequestIdleCallbackOptions
    ) => RequestIdleCallbackHandle;
    cancelIdleCallback?: (handle: RequestIdleCallbackHandle) => void;
  }
}

// Track which chunks have already been prefetched to avoid duplicates
const prefetchedChunks = new Set<string>();
const prefetchedRouteModules = new Set<string>();
const isPrerenderRuntime =
  typeof navigator !== "undefined" &&
  (navigator.userAgent.toLowerCase().includes("headless") ||
    navigator.webdriver === true);

/**
 * Route type categories
 */
type RouteCategory = 'candidate' | 'admin' | 'public' | 'auth';

/**
 * Chunk definitions for different route categories
 * These map to the webpackChunkName comments in router/index.ts
 */
const CHUNK_CATEGORIES: Record<RouteCategory, string[]> = {
  // Candidate-facing assessment pages - minimal prefetch
  candidate: [
    'confirm-canditate',
    'redirect-page',
    'assessment-test',
    // Assessment step components are lazy-loaded by AssessmentTest.vue
    // They don't need prefetching as they load on-demand per step
  ],
  
  // Admin/authenticated pages - full admin experience
  admin: [
    'assessments',
    'assessments-create',
    'AssessmentsDetails',
    'AssessmentsCandidate',
    'account',
    'respondents',
    'assessment-results',
    'assessment-group-analytics',
  ],
  
  // Public pages (core values analyzer, etc.)
  public: [
    'core-values-analyzer',
    '404',
  ],
  
  // Auth pages are prefetched with real dynamic imports so hashed chunk URLs
  // resolve correctly in production builds.
  auth: [],
};

type RouteModulePrefetcher = {
  key: string;
  load: () => Promise<unknown>;
};

const AUTH_ROUTE_PREFETCHERS: Array<{
  matches: (path: string) => boolean;
  routes: RouteModulePrefetcher[];
}> = [
  {
    matches: (path: string) => path.includes('/signup/branding'),
    routes: [],
  },
  {
    matches: (path: string) => path.includes('/signup/info'),
    routes: [
      {
        key: 'signup-branding',
        load: () =>
          import(
            /* webpackChunkName: "signup-branding" */ "@/views/Login/Branding.vue"
          ),
      },
    ],
  },
  {
    matches: (path: string) =>
      path.includes('/signup/verify') || path.includes('/account/verify'),
    routes: [
      {
        key: 'signup-info',
        load: () =>
          import(
            /* webpackChunkName: "signup-info" */ "@/views/Login/Info.vue"
          ),
      },
      {
        key: 'signup-branding',
        load: () =>
          import(
            /* webpackChunkName: "signup-branding" */ "@/views/Login/Branding.vue"
          ),
      },
    ],
  },
  {
    matches: (path: string) => path === '/signup' || path === '/th/signup',
    routes: [
      {
        key: 'verify',
        load: () =>
          import(
            /* webpackChunkName: "verify" */ "@/views/Login/Pincode.vue"
          ),
      },
      {
        key: 'signup-info',
        load: () =>
          import(
            /* webpackChunkName: "signup-info" */ "@/views/Login/Info.vue"
          ),
      },
    ],
  },
  {
    matches: (path: string) => path === '/login' || path === '/th/login',
    routes: [
      {
        key: 'signup',
        load: () =>
          import(
            /* webpackChunkName: "signup" */ "@/views/Login/Signup.vue"
          ),
      },
      {
        key: 'verify',
        load: () =>
          import(
            /* webpackChunkName: "verify" */ "@/views/Login/Pincode.vue"
          ),
      },
    ],
  },
];

/**
 * Determine the route category based on the current path
 */
export function getRouteCategory(path: string): RouteCategory {
  const lowerPath = path.toLowerCase();
  
  // Candidate assessment routes
  if (lowerPath.includes('/assessment-test')) {
    return 'candidate';
  }
  
  // Auth routes
  if (lowerPath.includes('/login') || lowerPath.includes('/signup') || lowerPath.includes('/pincode')) {
    return 'auth';
  }
  
  // Public routes (no auth required)
  if (
    lowerPath.includes('/core-values-analyzer') ||
    lowerPath.includes('/404') ||
    lowerPath === '/'
  ) {
    return 'public';
  }
  
  // Everything else is admin
  return 'admin';
}

/**
 * Create a prefetch link element and add it to the document head
 */
function createPrefetchLink(href: string): void {
  if (prefetchedChunks.has(href)) {
    return;
  }
  
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  link.as = href.endsWith('.css') ? 'style' : 'script';
  
  document.head.appendChild(link);
  prefetchedChunks.add(href);
}

/**
 * Get the list of assets (JS and CSS) for a given chunk name
 * This reads from webpack's manifest if available, otherwise constructs paths
 */
function getChunkAssets(chunkName: string): string[] {
  const assets: string[] = [];
  
  // In production, assets have content hashes
  // We'll use a pattern-based approach since we don't have access to the manifest at runtime
  // The actual files will be resolved by the browser
  assets.push(`/js/${chunkName}.js`);
  assets.push(`/css/${chunkName}.css`);
  
  return assets;
}

function scheduleIdlePrefetch(task: () => void): void {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => task(), { timeout: 2000 });
  } else {
    setTimeout(task, 1);
  }
}

/**
 * Prefetch chunks for a specific route category
 */
export function prefetchForCategory(category: RouteCategory): void {
  const chunks = CHUNK_CATEGORIES[category];
  
  if (!chunks || chunks.length === 0) {
    return;
  }
  
  // Use requestIdleCallback if available for better performance
  // Falls back to setTimeout for browsers that don't support it
  const prefetchChunks = () => {
    chunks.forEach(chunkName => {
      const assets = getChunkAssets(chunkName);
      assets.forEach(asset => createPrefetchLink(asset));
    });
  };
  
  scheduleIdlePrefetch(prefetchChunks);
}

function prefetchAuthRoute(path: string): void {
  if (isPrerenderRuntime) {
    return;
  }

  const normalizedPath = path.toLowerCase();
  const prefetchGroup = AUTH_ROUTE_PREFETCHERS.find(({ matches }) =>
    matches(normalizedPath)
  );

  if (!prefetchGroup || prefetchGroup.routes.length === 0) {
    return;
  }

  scheduleIdlePrefetch(() => {
    prefetchGroup.routes.forEach(({ key, load }) => {
      if (prefetchedRouteModules.has(key)) {
        return;
      }

      prefetchedRouteModules.add(key);
      load().catch(() => {
        prefetchedRouteModules.delete(key);
      });
    });
  });
}

export function prefetchAuthRouteOnDemand(path: string): void {
  prefetchAuthRoute(path);
}

/**
 * Main function to handle route-based prefetching
 * Call this in router.afterEach()
 */
export function handleRoutePrefetch(path: string): void {
  const category = getRouteCategory(path);
  
  // For candidate routes, we intentionally prefetch very little
  // This reduces initial requests from 125+ to ~15-20
  if (category === 'candidate') {
    // Don't prefetch anything extra for candidates
    // The assessment step components load on-demand as needed
    return;
  }
  
  // Don't prefetch on 404 pages - user may have come from anywhere
  if (path.includes('/404') || path === '/404') {
    return;
  }
  
  // For auth routes, prefetch other auth pages
  if (category === 'auth') {
    if (isInitialPublicAuthPath(path)) {
      return;
    }
    prefetchAuthRoute(path);
    return;
  }

  // Admin/public chunk prefetching is intentionally disabled here because the
  // link-based helper below cannot resolve hashed production asset filenames.
  // Keep auth prefetching only, since that path uses real dynamic imports.
}

/**
 * Check if the current route is a candidate-facing assessment page
 * Useful for other optimizations (e.g., font loading)
 */
export function isCandidateRoute(path: string): boolean {
  return getRouteCategory(path) === 'candidate';
}

/**
 * Check if Thai language fonts should be loaded
 * Based on route path containing /th/ or query param
 */
export function shouldLoadThaiFonts(path: string, query?: Record<string, string>): boolean {
  const lowerPath = path.toLowerCase();
  
  // Check path for Thai language indicator
  if (lowerPath.startsWith('/th/') || lowerPath.includes('/th/')) {
    return true;
  }
  
  // Check query params
  if (normalizeLanguageKey(query?.lang || "") === "th") {
    return true;
  }
  
  return false;
}

// Track if Thai fonts have been loaded
let thaiFontsLoaded = false;

/**
 * Dynamically load Thai fonts when needed (e.g., user switches language at runtime)
 * This is called from components or i18n when language changes to Thai
 */
export function loadThaiFonts(): void {
  if (thaiFontsLoaded) {
    return;
  }
  
  const fonts = [
    'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;500;600;700&display=swap',
    'https://fonts.googleapis.com/css2?family=Kanit:wght@400;500;600;700;800&display=swap'
  ];
  
  fonts.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  });
  
  thaiFontsLoaded = true;
}

/**
 * Check if Thai fonts are loaded
 */
export function areThaiFontsLoaded(): boolean {
  return thaiFontsLoaded;
}

export default {
  handleRoutePrefetch,
  getRouteCategory,
  prefetchForCategory,
  prefetchAuthRouteOnDemand,
  isCandidateRoute,
  shouldLoadThaiFonts,
  loadThaiFonts,
  areThaiFontsLoaded,
};
